import React, { useMemo } from 'react';
import type {
  ColDef,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getIndices, saveIdxBatch } from '../../api/idx';
import { useIdxTypes } from '../../hooks/useIdxTypes';
import { useCrcies } from '../../hooks/useCrcies';
import type { Idx } from '../../types/Idx';
import { t } from '../../i18n/t';

const newIdxDefaults = (): Partial<Idx> => ({
  idxId: 0,
  idxName: '',
  symbol: null,
  taseIdxId: null,
  // Default to the first type so the dropdown shows a valid value on Add
  // (idxTypeId is NOT NULL — no "none" option exists).
  idxTypeId: 1,
  crcyId: null,
});

export function IdxPage() {
  // Lazy load: the index types list is fetched only when this page mounts.
  const { data: idxTypes, loading: typesLoading, error: typesError } =
    useIdxTypes();
  const { data: crcies, loading: crciesLoading, error: crciesError } =
    useCrcies();

  const columnDefs = useMemo<ColDef<Idx>[]>(() => {
    if (!idxTypes || !crcies) return [];
    // Index currency: nullable FK to CRCY -> "(ללא)" option + "<code> - <name>" labels.
    const noneLabel = t('idx.noneOption');
    const crcyValues = [
      noneLabel,
      ...crcies.map((c) => `${c.crcyId} - ${c.crcyName}`),
    ];
    const crcyLabelById = new Map<number, string>(
      crcies.map((c) => [c.crcyId, `${c.crcyId} - ${c.crcyName}`]),
    );
    const crcyIdByLabel = new Map<string, number>(
      crcies.map((c) => [`${c.crcyId} - ${c.crcyName}`, c.crcyId]),
    );
    // Index-type names are unique (only 2 rows), so a plain name↔id map is
    // enough — no "<code> - <name>" disambiguation needed (unlike Categories).
    const dropdownValues = idxTypes.map((tp) => tp.idxTypeName);
    const nameById = new Map<number, string>(
      idxTypes.map((tp) => [tp.idxTypeId, tp.idxTypeName]),
    );
    const idByName = new Map<string, number>(
      idxTypes.map((tp) => [tp.idxTypeName, tp.idxTypeId]),
    );

    return [
      {
        field: 'idxId',
        headerName: t('idx.columns.idxId'),
        width: 80,
        // IDENTITY PK assigned by the server — read-only always (even on Add).
        editable: false,
        cellDataType: 'number',
        sort: 'asc',
        valueFormatter: (p: ValueFormatterParams<Idx>) => {
          const isAdded =
            (p.data as { __status?: string } | undefined)?.__status === 'added';
          if (isAdded) return '';
          return p.value == null ? '' : String(p.value);
        },
      },
      {
        field: 'idxName',
        headerName: t('idx.columns.idxName'),
        flex: 1,
        editable: true,
      },
      {
        field: 'symbol',
        headerName: t('idx.columns.symbol'),
        flex: 1,
        editable: true,
      },
      {
        field: 'taseIdxId',
        headerName: t('idx.columns.taseIdxId'),
        width: 140,
        editable: true,
        cellDataType: 'number',
      },
      {
        field: 'idxTypeId',
        headerName: t('idx.columns.idxTypeId'),
        width: 180,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: dropdownValues },
        valueGetter: (p: ValueGetterParams<Idx>) => {
          if (!p.data) return '';
          return nameById.get(p.data.idxTypeId) ?? '';
        },
        valueSetter: (p: ValueSetterParams<Idx>) => {
          if (!p.data) return false;
          const newId = idByName.get(p.newValue);
          if (newId === undefined) return false;
          p.data.idxTypeId = newId;
          return true;
        },
      },
      {
        field: 'crcyId',
        headerName: t('idx.columns.crcyId'),
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: crcyValues },
        valueGetter: (p: ValueGetterParams<Idx>) => {
          if (!p.data || p.data.crcyId == null) return noneLabel;
          return crcyLabelById.get(p.data.crcyId) ?? String(p.data.crcyId);
        },
        valueSetter: (p: ValueSetterParams<Idx>) => {
          if (!p.data) return false;
          if (p.newValue === noneLabel) {
            p.data.crcyId = null;
            return true;
          }
          const newId = crcyIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.crcyId = newId;
          return true;
        },
      },
    ];
  }, [idxTypes, crcies]);

  const loadError = typesError ?? crciesError;
  if (loadError) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('idx.errorPrefix', { message: loadError })}
        </div>
      </div>
    );
  }

  if (typesLoading || crciesLoading || !idxTypes || !crcies) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>;
  }

  return (
    <LopCrudPage<Idx>
      resourceCode="idx"
      titleKey="idx.pageTitle"
      idField="idxId"
      getRows={getIndices}
      saveBatch={(added, modified, deletedIds) =>
        saveIdxBatch(added, modified, deletedIds as number[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newIdxDefaults}
    />
  );
}
